// Chapter 2 of the detective game: "발화" (see Detective.js).
// Everything the player reads lives here; Detective.js only knows how to play it.

const n = (text) => ({ who: 'narr', text });
const me = (text) => ({ who: 'me', text });
const say = (who, text) => ({ who, text });

export const META = {
  id: 'ch2',
  number: 2,
  hanja: '發火',
  name: '발화',
  subtitle: '대성제분 화재 사건',
  tagline: '불은 시작된 자리를 기억한다.',
  saveKey: 'detective_progress_ch2',
  startLoc: 'warehouse',
  // The in-game clock runs on clues found, in minutes from Tuesday midnight.
  clock: { start: 300, end: 2700, days: ['화요일', '수요일'], limit: '기한 목요일 08:00' },
};

export const SPEAKERS = {
  me: { name: '서지안', title: '형사', tone: 'me' },
  boss: { name: '박 반장', title: '강력2팀장', tone: 'gray' },
  stranger: { name: '낯선 남자', tone: 'blue' },
  eunho: { name: '윤은호', title: '국과수 연구관', tone: 'blue' },
  doc: { name: '법의관', title: '국과수', tone: 'gray' },
  recep: { name: '접수 직원', title: '서해 미소치과', tone: 'gray' },
  gosi: { name: '고시원 총무', title: '한솔고시원', tone: 'gray' },
  oh: { name: '오대성', title: '사장', tone: 'amber' },
  jun: { name: '오준영', title: '전무', tone: 'green' },
  bae: { name: '배동수', title: '공장장', tone: 'orange' },
  wife: { name: '문정아', title: '한기섭의 아내', tone: 'rose' },
  noh: { name: '노태준', title: '보험사 조사원', tone: 'violet' },
  go: { name: '고 소장', title: '새벽인력', tone: 'gray' },
  han: { name: '한기섭', title: '재무이사', tone: 'red' },
};

export const CLUES = {
  breaker: {
    name: '꺼져 있던 차단기',
    kind: '물증',
    text: '창고 분전반의 주 차단기 손잡이가 맨 아래 「꺼짐」에 있다. 합선으로 떨어지면 가운데에 서는 기종이다. 불이 나기 전에 누가 손으로 내렸다.',
  },
  beads: {
    name: '녹은 구리 방울',
    kind: '물증',
    text: '1차 소견이 합선의 근거로 든 것. 녹았다는 것만 말해 줄 뿐, 불의 원인인지 결과인지는 말해 주지 않는다.',
  },
  twoOrigins: {
    name: '두 개의 발화 지점',
    kind: '물증',
    text: '분전반 앞과 사무실 안. 그 사이 바닥은 훨씬 덜 탔다. 불은 따로, 두 번 붙었다.',
  },
  boxes: {
    name: '분전반 밑의 잿더미',
    kind: '물증',
    text: '출고장에 있어야 할 포장 상자가 분전반 밑에 쌓여 있었다. 재 속에서 시간을 두고 불이 붙게 하는 장치의 잔해가 나왔다.',
  },
  bodySite: {
    name: '움직이지 않은 시신',
    kind: '물증',
    text: '책상 옆 바닥에 반듯이 누운 자세. 잠기지 않은 문까지 두 걸음인데 한 뼘도 움직이지 않았다.',
  },
  kerosene: {
    name: '등유',
    kind: '물증',
    text: '사무실 바닥에서 검출. 가장 짙은 곳은 머리와 양손이 있던 자리다. 분전반 쪽에서는 나오지 않았다.',
  },
  dvr: {
    name: '불탄 녹화기',
    kind: '물증',
    text: '창고 구역 CCTV의 녹화기는 사무실 캐비닛 안에 있었다. 복구할 수 없다.',
  },
  gateCctv: {
    name: '정문 CCTV',
    kind: '물증',
    text: '한기섭의 차는 전날 18시부터 주차장에. 공장장 23:05~23:20. 전무의 차는 01:55에 정문 앞에 서서 02:13에 떠났다. 뒤편 쪽문에는 카메라가 없다.',
  },
  panelLog: {
    name: '화재 수신기 기록',
    kind: '물증',
    text: '2월 21일 제품 창고 경보 차단. 2월 28일 창고 스프링클러 밸브 폐쇄. 불이 난 밤 창고에서는 아무것도 울리지 않았다.',
  },
  checklist: {
    name: '점검표의 메모',
    kind: '물증',
    text: '유지보수 기사의 글씨. 「차단 상태로 운영 시 화재 감지 불가. 복구 요망. 관리자 서명 거부.」 서명은 흘려 써서 읽기 어렵다. 내 눈에는 윤, ㅅ, ㅎ. 휴대폰으로 찍어 뒀다.',
  },
  audit: {
    name: '감사 통보서',
    kind: '물증',
    text: '한기섭의 책상 서랍에서 나왔다. 3월 18일 월요일부터 외부 회계감사. 대표이사 직권.',
  },
  desk: {
    name: '빈 여권 케이스와 빈 액자',
    kind: '물증',
    text: '한기섭의 책상. 이름이 박힌 여권 케이스는 비어 있고, 액자에는 사진이 붙어 있던 자국만 남았다.',
  },
  valveForm: {
    name: '밸브 폐쇄 요청서',
    kind: '물증',
    text: '「천장 누수 보수를 위한 스프링클러 알람밸브 임시 폐쇄.」 기안 한기섭, 결재 한기섭. 공사는 실제로 없었다.',
  },
  ledger: {
    name: '이중장부',
    kind: '물증',
    text: '경리실 금고에서 나왔다. 「원료 선급금」 명목으로 6년간 빠져나간 23억 4천만 원.',
  },
  stove: {
    name: '빈 등유통',
    kind: '물증',
    text: '제분동 휴게실 난로용 20리터 통. 3월 6일에 가득 채웠는데 비어 있다. 휴게실 열쇠는 사무동 열쇠함에 있다.',
  },
  collector: {
    name: '멈춘 집진기',
    kind: '물증',
    text: '제분동 2층. 1년째 사용 중지인데 점검구 볼트 네 개만 먼지가 없고 공구 자국이 새것처럼 반짝인다.',
  },
  ring: {
    name: '새끼손가락의 반지',
    kind: '물증',
    text: '유류품은 시계, 사원증, 금반지. 반지는 약지가 아니라 왼손 새끼손가락에서 나왔다.',
  },
  dental: {
    name: '치과 기록',
    kind: '물증',
    text: '서해 미소치과, 환자명 한기섭. 올해 1월에 세 번 치료하고 전체 X선을 찍었다. 그 X선이 시신의 치아와 일치했다.',
  },
  autopsy: {
    name: '부검 소견',
    kind: '물증',
    text: '기도에 그을음이 없고 혈중 일산화탄소는 정상 범위. 목 앞쪽의 작은 뼈가 부러져 있다. 불이 나기 전에 목이 졸려 죽었다.',
  },
  xray: {
    name: '발목 X선',
    kind: '물증',
    text: '시신의 양쪽 발목에 금속이 없다. 뼈가 부러졌다 붙은 자국도 없다.',
  },
  signature: {
    name: '치과 접수증의 서명',
    kind: '물증',
    text: '접수증의 「한기섭」 서명이 회사 결재 서류의 글씨와 다르다. 직원은 건강보험증만 낸 작업복 차림의 남자를 기억한다.',
  },
  chun: {
    name: '일 나간 사람 장부',
    kind: '물증',
    text: '대성제분 야간 일용직은 석 달 내내 천영복(49) 한 사람. 마지막 기록은 3월 8일. 한기섭이 「천 씨는 그만뒀다」고 전화했다.',
  },
  room: {
    name: '고시원 방',
    kind: '물증',
    text: '쓰레기통에 염색약 상자와 새 면도기 포장, 3월 9일 자 마트 영수증. 영수증에는 몽키스패너와 큰 여행 가방도 찍혀 있다. 뒷면에 메모 「배는 목요일뿐. 05시 삼덕항. 현금만.」',
  },
  ohAlibi: {
    name: '사장의 통행 기록',
    kind: '물증',
    text: '20:10 서해 요금소로 고속도로에 올라 05:10 같은 요금소로 내려왔다. 그 사이 부산의 장례식장에 있었다.',
  },
  call119: {
    name: '02시 12분의 신고',
    kind: '물증',
    text: '119 상황실 기록. 02:12, 이름을 밝히지 않은 남자가 대성제분 화재를 처음 신고했다. 전화를 건 번호는 전무 오준영의 휴대폰이다.',
  },
  text0120: {
    name: '01시 20분의 문자',
    kind: '물증',
    text: '한기섭의 번호에서 전무에게. 「2시에 공장 정문에서 뵙겠습니다. 혼자 오십시오.」 전무는 이 문자를 받고 정문 앞에 나와 있었다.',
  },
  fireInsurance: {
    name: '화재보험',
    kind: '증언',
    text: '보험사 조사원의 말. 제품 창고 12억. 장부상 재고가 창고에 들어가지도 않을 만큼 부풀려져 있다.',
  },
  lifeInsurance: {
    name: '사망보험',
    kind: '증언',
    text: '보험사 조사원의 말. 한기섭이 작년 12월 가입. 사고로 죽으면 10억, 수익자는 아내. 가입 2년 안의 자살은 지급되지 않는다.',
  },
  schedule: {
    name: '재고조사 일정',
    kind: '증언',
    text: '보험사 조사원의 말. 목요일 08시에 조사팀이 제분동과 설비까지 전부 뜯어 본다. 그 전에는 통제선 때문에 아무도 못 들어간다.',
  },
  baeSaw: {
    name: '공장장이 본 것',
    kind: '증언',
    text: '23시경 창고 사무실에서 한기섭이 일용직 천영복과 소주를 마시고 있었다. 한기섭이 계속 잔을 채웠고 천영복은 이미 고개가 꺾여 있었다.',
  },
  tenYears: {
    name: '10년 동안 가지 않은 치과',
    kind: '증언',
    text: '문정아의 말. 남편은 치과를 무서워해서 10년을 가지 않았다. 올 1월에도 치과에 다녀온 기색이 없었다.',
  },
  ankle: {
    name: '발목의 철심',
    kind: '증언',
    text: '문정아의 말. 남편은 10년 전 교통사고로 왼쪽 발목에 철심을 박았다. 공항 검색대에서 소리가 난다.',
  },
  cremate: {
    name: '화장 부탁',
    kind: '증언',
    text: '문정아의 말. 남편이 올겨울부터 「무슨 일 생기면 화장부터 해 달라」고 여러 번 다짐을 받았다. 화장은 목요일 아침.',
  },
  sameBuild: {
    name: '꼭 천 씨만',
    kind: '증언',
    text: '고 소장의 말. 한기섭은 천영복만 찾았다. 체격이 비슷해 헌 옷을 물려주기 좋다고 했다. 천영복은 「이사님 덕에 이도 고쳤다」고 했다.',
  },
  alive: {
    name: '돌아온 천영복',
    kind: '증언',
    text: '고시원 총무의 말. 천영복은 불이 난 날 새벽 세 시쯤 방에 들어왔다. 모자와 마스크를 쓰고, 인사에 대꾸가 없었다. 날이 밝자 가방 하나를 들고 나가 돌아오지 않았다.',
  },
  dust: {
    name: '폭발의 다섯 조건',
    kind: '증언',
    text: '윤은호의 설명. 가루, 산소, 불씨, 떠 있는 먼지, 갇힌 공기. 하나만 빠져도 터지지 않는다. 스위치의 불꽃과 총구의 화염도 불씨다.',
  },
};

export const INTRO = [
  n('3월 12일 화요일, 새벽 4시. 서해시 외곽의 제분 공장, 대성제분.'),
  n("넉 달 전 412호 사건 뒤로 서에서 나를 부르는 말이 하나 늘었다. '사고를 사건으로 만드는 애.' 칭찬은 아니다."),
  say('boss', '제분 공장 창고에 불이 났다. 안에서 한 명이 죽었고. 소방은 합선으로 본다.'),
  say('boss', '이번 건은 네가 맡아라. 사고면 사고라고 쓰고, 아니면 아니라고 써. 대신 증거로 해.'),
  say('boss', '증거가 모자라면 사고인 거야. 그게 규칙이다.'),
  n('박 반장이 사건을 통째로 넘긴 것은 처음이다. 그는 내 눈을 보지 않고 서류철만 내밀었다.'),
  n('불은 새벽 2시 10분에 났고 3시 30분에 잡혔다. 제품 창고 절반이 탔다. 창고 안쪽 사무실에서 시신 한 구가 나왔다.'),
  n('재무이사 한기섭, 47세. 손목의 시계와 사원증이 본인 것이었다. 치과 기록 대조도 오늘 중에 끝난다고 했다. 결산을 앞두고 밤새 재고를 세던 중이었다고 했다.'),
  n('새벽 5시. 감식반이 오기 전에 현장을 한 번 보고 싶었다. 통제선을 넘자, 잿더미 위에 손전등 불빛 하나가 쪼그려 앉아 있었다.'),
  me('경찰입니다. 거기서 손 떼고 일어나세요.'),
  say('stranger', '잠깐만요. 이거 하나만 찍고요.'),
  n('현장에 몰래 들어온 사람이 경찰한테 기다리라고 한다.'),
  me('셋 셉니다. 하나.'),
  say('stranger', '형사님, 지금 밟고 계신 데가 발화 지점입니다. 왼발 좀.'),
  n('나는 왼발을 들었다. 그는 그 자리를 찍고 나서야 일어나 신분증을 내밀었다.'),
  say('eunho', '국과수 윤은호입니다. 감식은 9시인데, 해 뜰 때 그림자가 길어야 바닥 무늬가 잘 보여서요.'),
  say('eunho', '그리고 형사님. 여기, 합선으로 난 불이 아닌 것 같습니다.'),
  say('sys', '공장을 조사하고, 사람들을 심문하고, 단서를 모으세요. 준비가 되면 [추리하기]로 박 반장에게 보고합니다. 기한은 목요일 아침 8시.'),
];

export const LOCATIONS = [
  {
    id: 'warehouse',
    name: '제품 창고',
    desc: '밀가루 포대를 쌓아 두던 창고. 천장 절반이 내려앉았고 바닥은 소방수와 재가 섞여 질척인다. 탄 밀가루 냄새가 빵 굽는 냄새와 닮아서 더 고약하다.',
    hotspots: [
      {
        id: 'panel',
        label: '분전반을 본다',
        variants: [
          {
            lines: [
              n('벽의 분전반. 소방의 1차 소견이 발화 지점으로 꼽은 곳이다. 문짝이 녹아 뒤틀렸다.'),
              n('윤은호가 장갑 낀 손가락으로 안쪽의 큰 손잡이를 가리켰다.'),
              say('eunho', "주 차단기 손잡이를 보세요. 맨 아래, '꺼짐'에 가 있죠."),
              say('eunho', '합선이 나면 차단기는 스스로 떨어집니다. 이 기종은 그럴 때 손잡이가 가운데에서 멈춰요. 제조사 자료로 확인했습니다.'),
              say('eunho', '맨 아래까지 내려가 있다는 건, 불이 나기 전에 누가 손으로 내렸다는 뜻입니다. 이 분전반에는 전기가 흐르지 않고 있었어요.'),
              n('전기가 끊긴 선에서는 합선이 일어날 수 없다.'),
            ],
            gives: ['breaker'],
          },
        ],
      },
      {
        id: 'wire',
        label: '녹은 전선을 본다',
        variants: [
          {
            lines: [
              n("분전반 아래로 늘어진 전선 끝에 구리가 방울져 굳어 있다. 1차 소견서가 '합선의 흔적'이라고 적은 것이다."),
              say('eunho', '구리는 천 도 남짓이면 녹습니다. 이만한 불이면 전기가 없어도 녹아요.'),
              say('eunho', "녹은 자국은 '녹았다'는 것만 말해 줍니다. 그게 불의 원인인지 결과인지는 말해 주지 않아요. 그건 다른 증거로 정해야 합니다."),
            ],
            gives: ['beads'],
          },
        ],
      },
      {
        id: 'floor',
        label: '바닥의 탄 자국을 본다',
        variants: [
          {
            lines: [
              n('윤은호가 바닥에 분필로 두 군데 동그라미를 쳤다. 분전반 앞, 그리고 안쪽 사무실의 책상 옆.'),
              say('eunho', '불은 위로, 그리고 바깥으로 번집니다. 가장 깊게, 가장 오래 탄 자리가 시작점이에요. 여기 두 곳입니다.'),
              say('eunho', '중요한 건 그 사이예요. 두 지점 사이 바닥은 훨씬 덜 탔습니다. 한쪽 불이 번져서 다른 쪽에 닿은 게 아니라는 뜻입니다.'),
              n('불은 따로, 두 번 붙었다.'),
            ],
            gives: ['twoOrigins'],
          },
        ],
      },
      {
        id: 'ash',
        label: '분전반 밑의 잿더미를 본다',
        variants: [
          {
            lines: [
              n('분전반 바로 밑에 종이 재가 무릎 높이로 쌓여 있다. 포장용 골판지 상자다. 상자는 원래 창고 반대편 출고장에 쌓아 두는 물건이다.'),
              n('재를 걷어 내자 녹아 붙은 플라스틱과 작은 금속 부품이 나왔다.'),
              say('eunho', '시간을 두고 불이 붙게 하는 장치의 잔해로 보입니다. 정확한 건 분석을 해 봐야 하지만요.'),
              n('누군가 분전반 밑에 탈 것을 쌓고, 자리를 뜬 뒤에 불이 붙도록 해 놓았다. 전기 화재처럼 보이라고.'),
            ],
            gives: ['boxes'],
          },
        ],
      },
    ],
  },
  {
    id: 'office',
    name: '창고 사무실',
    desc: '창고 안쪽을 칸막이로 막은 세 평짜리 사무실. 책상과 캐비닛, 간이침대가 있었다. 지금은 뼈대만 남았다. 시신은 이 방 바닥에서 나왔다.',
    hotspots: [
      {
        id: 'site',
        label: '시신이 있던 자리를 본다',
        variants: [
          {
            lines: [
              n('바닥에 사람 모양으로 덜 탄 자리가 남아 있다. 책상 옆, 반듯이 누운 자세.'),
              n('문까지는 두 걸음이다. 문은 잠겨 있지 않았다.'),
              n('불 속에서 깬 사람은 문 쪽으로 기어간다. 그래서 문 앞에서, 엎드린 채로 발견된다. 이 사람은 누운 자리에서 한 뼘도 움직이지 않았다.'),
            ],
            gives: ['bodySite'],
          },
        ],
      },
      {
        id: 'stain',
        label: '바닥의 얼룩을 본다',
        variants: [
          {
            lines: [
              n('시신이 있던 자리 둘레로 바닥이 유난히 깊게 패어 탔다. 따라 들어온 윤은호가 금속 캔에 잔해를 퍼 담으며 말했다.'),
              say('eunho', '현장에서 기름 성분을 잡아내는 간이 검사를 먼저 해 봤습니다. 이 방 바닥에서 기름이 나옵니다. 냄새와 반응으로 보면 등유예요. 정밀 분석은 따로 보냈고요.'),
              say('eunho', '가장 짙은 곳이 두 군데입니다. 머리가 있던 자리, 그리고 양손이 있던 자리.'),
              say('eunho', '분전반 쪽에서는 등유가 안 나옵니다. 저쪽은 종이, 이쪽은 기름. 붙인 방식이 달라요.'),
              n('가장 짙은 두 곳. 머리와 손.'),
            ],
            gives: ['kerosene'],
          },
        ],
      },
      {
        id: 'cabinet',
        label: '캐비닛의 잔해를 본다',
        variants: [
          {
            lines: [
              n('캐비닛 안에서 검게 녹은 기계 덩어리가 나왔다. 창고 구역 CCTV의 녹화기다.'),
              n('창고 안팎을 찍은 영상은 전부 이 상자 안에 있었다. 복구는 불가능하다.'),
              n('불이 꼭 태워야 했던 것이 이 방에 적어도 둘 있었다. 녹화기, 그리고 시신.'),
            ],
            gives: ['dvr'],
          },
        ],
      },
    ],
  },
  {
    id: 'admin',
    name: '사무동',
    desc: '2층짜리 사무 건물. 1층이 경리실, 2층이 사장실이다. 불은 여기까지 오지 않았다. 직원들은 출근했지만 아무도 일을 하지 않는다.',
    hotspots: [
      {
        id: 'hanDesk',
        label: '한기섭의 책상을 본다',
        variants: [
          {
            lines: [
              n('경리실 창가, 재무이사의 책상. 20년 차 직장인의 책상답게 정돈돼 있다.'),
              n('서랍 맨 위에 구겨졌다 다시 편 공문 한 장. 「외부 회계감사 실시 통보. 3월 18일(월)부터. 대표이사 직권.」'),
              n('다음 주 월요일이다.'),
            ],
            gives: ['audit'],
          },
        ],
      },
      {
        id: 'hanDrawer',
        label: '책상 서랍을 끝까지 열어 본다',
        showWhen: { clue: 'tenYears' },
        variants: [
          {
            lines: [
              n('한기섭의 책상으로 돌아와 아래 서랍까지 열었다. 가죽 여권 케이스가 있다. 이름이 금박으로 박혀 있다. 열어 보니 비어 있다.'),
              n('책상 위 액자를 다시 봤다. 이것도 비어 있다. 유리 안쪽에 사진이 붙어 있던 자국만 네모나게 남았다.'),
              n('밤새 재고를 세겠다던 사람의 책상에서, 여권과 가족사진이 빠져나갔다.'),
            ],
            gives: ['desk'],
          },
        ],
      },
      {
        id: 'files',
        label: '경리실 서류철을 본다',
        variants: [
          {
            lines: [
              n('시설 관련 품의서 철. 2월 27일 자 한 장이 눈에 걸렸다.'),
              n('「제품 창고 천장 누수 보수를 위한 스프링클러 알람밸브 임시 폐쇄 요청.」 기안 한기섭, 결재 한기섭.'),
              n('뒤에 붙어 있어야 할 공사 견적서도, 업체 계약서도 없다. 창고 직원들은 천장 공사를 본 적이 없다고 했다.'),
              n('죽은 사람이 왜, 자기가 죽게 될 창고의 물을 2주 전에 잠가 뒀을까.'),
            ],
            gives: ['valveForm'],
          },
        ],
      },
      {
        id: 'safe',
        label: '경리실 금고를 본다',
        showWhen: { flag: 'safeOpen' },
        variants: [
          {
            lines: [
              n('오 사장이 떨리는 손으로 금고 다이얼을 돌렸다. 6년 동안 한기섭 말고는 아무도 열어 본 적 없는 금고다.'),
              n('맨 아래 칸에 검은 장부가 한 권. 회사 전산의 숫자와 다른, 손으로 쓴 숫자들.'),
              n('「원료 선급금」이라는 이름으로 6년 동안 빠져나간 돈. 맨 끝 줄의 합계는 23억 4천만 원이다.'),
              say('oh', '…이게, 이게 다 뭐요. 한 이사가… 기섭이가?'),
            ],
            gives: ['ledger'],
          },
        ],
      },
    ],
  },
  {
    id: 'mill',
    name: '제분동',
    desc: '밀을 빻는 4층 건물. 화재 뒤로 가동을 멈췄다. 바닥과 난간, 머리 위 대들보까지 밀가루가 눈처럼 앉아 있다. 벽에는 붉은 글씨. 「분진 폭발 주의. 화기 엄금.」',
    hotspots: [
      {
        id: 'beam',
        label: '대들보의 먼지를 본다',
        variants: [
          {
            lines: [
              n('손전등을 위로 비췄다. 대들보 위에 밀가루가 손가락 한 마디 두께로 쌓여 있다.'),
              n('난간을 손바닥으로 치자 흰 먼지가 피어올라 한참을 떠 있었다. 숨을 쉴 때마다 목이 칼칼하다.'),
              n('이 건물에서는 담배 한 개비가 폭탄이라고, 공장장이 말했다.'),
            ],
          },
        ],
      },
      {
        id: 'stove',
        label: '휴게실의 난로를 본다',
        variants: [
          {
            lines: [
              n('1층 휴게실의 석유난로. 옆에 20리터짜리 등유통이 있다. 들어 보니 가볍다. 비었다.'),
              n('통 손잡이에 꼬리표가 달려 있다. 「3/6 가득 채움. 배」 지난주 수요일이다.'),
              n('휴게실 문은 퇴근 때 잠근다. 열쇠는 사무동 열쇠함에 걸어 둔다고 했다.'),
            ],
            gives: ['stove'],
          },
        ],
      },
      {
        id: 'collector',
        label: '2층의 낡은 집진기를 본다',
        variants: [
          {
            lines: [
              n('2층 구석에 방 하나만 한 철제 상자가 서 있다. 공기 중의 밀가루 먼지를 빨아들여 모으는 집진기다. 「사용 중지」 딱지가 1년째 붙어 있다고 했다.'),
              n('겉은 먼지투성이다. 그런데 옆구리 점검구를 잠근 볼트 네 개만 먼지가 없다. 볼트 머리에 공구가 물린 자국이 새것처럼 반짝인다.'),
              n('1년 동안 멈춰 있던 기계의 뚜껑을, 누군가 최근에 여러 번 열었다.'),
            ],
            gives: ['collector'],
          },
        ],
      },
    ],
  },
  {
    id: 'gate',
    name: '정문 경비실',
    desc: '공장 정문 옆 컨테이너 건물. 밤에는 사람이 없고 기계만 지킨다. 정문 CCTV의 녹화기와 화재 수신기가 이 안에 있다.',
    hotspots: [
      {
        id: 'cctv',
        label: '정문 CCTV를 본다',
        variants: [
          {
            lines: [
              n('정문 카메라는 차량 출입구와 앞 도로를 찍는다. 화재 전날 저녁부터 돌려 봤다.'),
              n('18시 2분, 한기섭의 흰색 승용차가 들어온다. 그 차는 지금도 주차장에 서 있다.'),
              n('23시 5분, 공장장 배동수의 화물차가 들어왔다가 23시 20분에 나간다.'),
              n('01시 55분, 전무 오준영의 검은 세단이 정문 앞 도로에 선다. 시동을 켠 채 서 있다가 02시 13분에 급히 떠난다.'),
              n('걸어서 정문을 드나든 사람은 없다. 다만 공장 뒤편 쪽문에는 카메라가 없다.'),
            ],
            gives: ['gateCctv'],
          },
        ],
      },
      {
        id: 'receiver',
        label: '화재 수신기를 본다',
        variants: [
          {
            lines: [
              n('벽에 붙은 화재 수신기. 기록을 거슬러 올라갔다.'),
              n('「2월 21일 제품 창고 구역 경보 — 차단」 「2월 28일 제품 창고 스프링클러 밸브 — 폐쇄」'),
              n('불이 난 밤, 창고의 감지기는 3주째 꺼져 있었고 스프링클러에는 2주째 물이 가지 않았다.'),
              n('창고가 타는 동안 이 기계는 한 번도 울리지 않았다.'),
            ],
            gives: ['panelLog'],
          },
        ],
      },
      {
        id: 'sheet',
        label: '수신기 옆의 점검표를 본다',
        variants: [
          {
            lines: [
              n('수신기 옆에 클립보드가 걸려 있다. 소방 설비 유지보수 업체의 월간 점검표다.'),
              n('2월 점검란에 볼펜으로 꾹꾹 눌러 쓴 메모. 「창고 구역 경보 차단 요청받음. 차단 상태로 운영 시 화재 감지 불가. 복구 요망.」'),
              n('그 아래 관리자 확인란은 비어 있고, 같은 글씨로 한 줄이 더 있다. 「관리자 서명 거부.」'),
              n('점검자 서명은 흘려 써서 읽기 어렵다. 윤, 그리고 ㅅ, ㅎ.'),
              n('경보가 꺼져 있다는 것을, 이 공장의 누군가는 경고까지 받고도 그대로 뒀다.'),
              n('불이 왜 커졌는지는 윤은호가 볼 일이다. 휴대폰으로 점검표를 찍어 그에게 보냈다. 경비실을 나서기 전에 전화가 왔다.'),
              say('eunho', '사진 받았습니다. …서명란을 한 장만 더 크게 찍어 주실 수 있습니까.'),
              me('아는 업체입니까.'),
              say('eunho', '…아닙니다. 아는 글씨 같아서요. 잘못 봤을 겁니다. 계속하시죠.'),
              n('윤 씨는 흔한 성이다. 나는 더 묻지 않았다.'),
              n('서명란을 한 장 더 찍었다. 사진은 지우지 않고 휴대폰에 남겨 뒀다.'),
            ],
            gives: ['checklist'],
          },
        ],
      },
    ],
  },
  {
    id: 'station',
    name: '서해경찰서',
    desc: '강력2팀 사무실. 내 책상 위에 이 사건의 서류가 쌓이기 시작했다. 박 반장은 창가 자리에서 믹스커피를 젓고 있고, 접견용 소파에는 보험사에서 나온 조사원이 앉아 있다.',
    hotspots: [
      {
        id: 'effects',
        label: '유류품을 본다',
        variants: [
          {
            lines: [
              n('증거물 봉투 세 개. 불에 그을린 금속 시계, 녹아 붙은 사원증, 그리고 금반지.'),
              n('시계 뒷면에 새긴 글씨가 남아 있다. 「10주년. 정아가.」 아내가 이 시계를 보고 남편을 알아봤다.'),
              n('시신을 수습할 때 적은 기록을 읽었다. 반지는 왼손 새끼손가락에서 나왔다. 약지가 아니라.'),
            ],
            gives: ['ring'],
          },
        ],
      },
      {
        id: 'idfile',
        label: '신원 확인 서류를 본다',
        showWhen: { flag: 'autopsy' },
        variants: [
          {
            lines: [
              n('부검 소견서와 함께 신원 확인 서류도 와 있다. 불에 탄 시신의 신원은 대개 치아로 확인한다. 이번에도 그랬다.'),
              n('「서해 미소치과. 환자명 한기섭. 1월 9일, 1월 16일, 1월 30일 충치 치료. 1월 9일 전체 X선 촬영.」'),
              n('그 X선과 시신의 치아가 일치했다. 그래서 이 시신은 한기섭이다.'),
              n('치아 기록이 맞으면 신원 확인은 거기서 끝난다. DNA까지 맡길 이유가 서류에는 없었다.'),
            ],
            gives: ['dental'],
          },
        ],
      },
      {
        id: 'autopsy',
        label: '부검 소견을 듣는다',
        showWhen: { flag: 'autopsy' },
        variants: [
          {
            lines: [
              n('소견서를 끝까지 읽고, 법의관이 당부한 대로 전화를 걸었다.'),
              say('doc', '서 형사님, 전화 주셨군요. 결론부터 말씀드리면, 이분은 불이 났을 때 숨을 쉬고 있지 않았습니다.'),
              say('doc', '살아서 연기를 마시면 기도 깊숙이, 성대 아래까지 그을음이 들어갑니다. 피에는 일산화탄소가 쌓이고요. 이분은 기도가 깨끗하고, 일산화탄소 수치도 정상 범위입니다.'),
              say('doc', '그것만으로 단정하지는 않습니다. 순식간에 번진 불에서는 수치가 낮게 나오기도 하니까요. 그래서 목을 봤습니다. 목 앞쪽의 작은 뼈가 부러져 있습니다. 목이 졸렸을 때 부러지는 뼈입니다.'),
              n('사고가 방화가 되더니, 방화가 살인이 됐다.'),
            ],
            gives: ['autopsy'],
          },
        ],
      },
      {
        id: 'xray',
        label: '발목 X선을 요청한다',
        showWhen: [{ clue: 'autopsy' }, { clue: 'ankle' }],
        variants: [
          {
            lines: [
              me('선생님, 부검 때 찍은 전신 X선에서 발목을 봐 주시겠어요. 10년 전에 박은 철심이 있어야 합니다.'),
              say('doc', '발목이요. …양쪽 다 깨끗합니다. 금속은 없습니다. 뼈가 부러졌다 붙은 자국도 없고요.'),
              say('doc', '철심은 불에 타지 않습니다. 있었다면 여기 하얗게 찍혔을 겁니다.'),
              n('한기섭의 발목에는 철심이 있다. 이 시신의 발목에는 없다.'),
            ],
            gives: ['xray'],
          },
        ],
      },
      {
        id: 'callDentist',
        label: '치과에 전화한다',
        showWhen: [{ clue: 'dental' }, { clue: 'tenYears' }],
        variants: [
          {
            lines: [
              n('미소치과의 접수 직원은 그 환자를 기억하고 있었다.'),
              say('recep', '작업복 차림이셨어요. 차트에는 이사님이라고 돼 있어서 좀 의아했죠. 건강보험증만 내셨고요.'),
              n('팩스로 받은 접수증의 서명을 회사 결재 서류의 서명과 나란히 놓았다. 같은 이름, 전혀 다른 글씨다.'),
            ],
            gives: ['signature'],
          },
        ],
      },
      {
        id: 'call119',
        label: '119 신고 기록을 받아 본다',
        showWhen: { flag: 'junSilent' },
        variants: [
          {
            lines: [
              n('전무가 입을 닫았으니 기록에 물을 차례다. 소방 상황실에 그날 밤의 신고 기록을 요청했다.'),
              n('첫 신고는 02시 12분. 남자 목소리다. 대성제분 창고에 불이 났다는 말만 하고, 이름을 묻자 끊었다.'),
              n('전화를 건 번호가 남아 있다. 오준영의 휴대폰이다.'),
              n('집에서 잤다던 사람이, 불이 난 지 2분 만에 그 불을 신고했다.'),
            ],
            gives: ['call119'],
          },
        ],
      },
    ],
  },
  {
    id: 'home',
    name: '한기섭의 집',
    desc: '공장에서 차로 15분 거리의 오래된 아파트. 현관에 남자 구두가 가지런히 놓여 있다. 문정아는 식탁에 앉아 식은 보리차 잔을 쥐고 있다. 아직 실감이 나지 않는다고 했다.',
    hotspots: [
      {
        id: 'entrance',
        label: '현관을 본다',
        variants: [
          {
            lines: [
              n('신발장 위에 가족사진이 있다. 한기섭과 문정아, 교복을 입은 딸. 한기섭은 사진 속에서 오른쪽 다리에 체중을 싣고 서 있다.'),
              n('구두는 전부 왼쪽 굽만 덜 닳았다. 왼발을 아끼며 걷는 사람의 신발이다.'),
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'agency',
    name: '새벽인력',
    showWhen: { clue: 'baeSaw' },
    desc: '역 뒤편 골목의 인력사무소. 새벽 다섯 시면 사람들이 줄을 서고 여덟 시면 텅 빈다. 고 소장이 장부를 펴 놓고 믹스커피를 내밀었다.',
    hotspots: [
      {
        id: 'book',
        label: '일 나간 사람 장부를 본다',
        variants: [
          {
            lines: [
              n('대성제분 야간 상하차. 12월 중순부터 석 달 동안 같은 이름이 줄을 잇는다. 천영복. 49세.'),
              n('마지막 기록은 3월 8일. 불이 나기 나흘 전이다. 그 옆에 소장의 메모. 「한 이사 전화. 천 씨 그만둠. 당분간 인력 필요 없음.」'),
              n('그런데 공장장은 불이 난 그 밤, 창고 사무실에서 천 씨를 봤다.'),
            ],
            gives: ['chun'],
          },
        ],
      },
    ],
  },
  {
    id: 'gosiwon',
    name: '한솔고시원',
    showWhen: { clue: 'chun' },
    desc: '인력사무소에서 두 골목 떨어진 고시원. 복도 양쪽으로 문이 스무 개쯤 늘어서 있다. 천영복의 방은 3층 끝, 창문 없는 방이다.',
    hotspots: [
      {
        id: 'manager',
        label: '총무에게 묻는다',
        variants: [
          {
            lines: [
              n('고시원 총무는 장부를 넘기며 귀찮은 듯 말했다.'),
              say('gosi', '천 씨요? 불난 날 새벽 세 시쯤 들어왔는데. 모자 쓰고 마스크 쓰고. 인사를 해도 대꾸가 없더라고. 원래는 인사성이 밝은 양반인데.'),
              say('gosi', '날 밝자마자 가방 하나 들고 나갔어. 그 뒤로는 못 봤고.'),
              n('공장장이 본 천영복은 고개가 꺾여 있었다. 그 사람이 몇 시간 뒤 제 발로 여기 들어왔다.'),
            ],
            gives: ['alive'],
          },
        ],
      },
      {
        id: 'room',
        label: '천영복의 방을 본다',
        variants: [
          {
            lines: [
              n('방은 치워져 있었다. 이불은 개어져 있고 옷걸이에는 작업복 한 벌만 남았다.'),
              n('쓰레기통에 염색약 상자와 새 면도기 포장, 그리고 구겨진 마트 영수증. 3월 9일. 「염색약 1, 면도기 1, 몽키스패너 1, 여행 가방(대) 1.」'),
              n('영수증 뒷면에 메모가 있다. 「배는 목요일뿐. 05시 삼덕항. 현금만.」'),
              n('목요일 새벽 5시. 누군가 그 시각에 이 도시를 떠날 생각이다.'),
            ],
            gives: ['room'],
          },
        ],
      },
    ],
  },
];

export const VICTIM = {
  id: 'victim',
  name: '한기섭',
  age: 47,
  role: '재무이사',
  tag: '피해자',
  desc: '대성제분에서 20년을 일한 재무이사. 결산을 앞두고 창고 사무실에서 밤새 재고를 세다 숨진 것으로 확인됐다.',
};

export const PEOPLE = [
  {
    id: 'eunho',
    name: '윤은호',
    age: 32,
    role: '국과수 연구관',
    tag: '감식',
    loc: 'warehouse',
    desc: '국립과학수사연구원 연구관. 화재와 안전사고의 원인 조사를 맡는다. 해가 뜨기 전에 혼자 현장에 와 있었다. 질문을 하면 대답보다 설명이 먼저 나온다.',
    topics: [
      {
        id: 'read',
        label: '불난 자리를 읽는 법',
        lines: [
          say('eunho', '불은 거짓말을 못 합니다. 위로 타고, 산소가 있는 쪽으로 가고, 오래 머문 자리를 깊게 팝니다. 사람이 뭘 꾸며도 그 순서는 못 바꿔요.'),
          say('eunho', '그래서 저는 사람 말보다 바닥을 먼저 봅니다. 아, 형사님 말을 안 믿는다는 뜻은 아니고요.'),
        ],
      },
      {
        id: 'dust',
        label: '제분소의 위험',
        lines: [
          say('eunho', '형사님, 제분동에 들어가실 거면 하나만 기억하세요. 밀가루는 터집니다.'),
          say('eunho', '조건이 다섯 가지예요. 가루, 산소, 불씨. 그리고 가루가 공기 중에 떠 있을 것, 그 공기가 갇혀 있을 것.'),
          say('eunho', '다섯 개가 다 모여야 터지고, 하나만 빠져도 안 터집니다. 불씨는 라이터만이 아니에요. 전등 스위치의 불꽃, 정전기, 총구의 화염. 전부 불씨입니다.'),
          say('eunho', '진짜 무서운 건 두 번째 폭발이에요. 첫 폭발이 대들보에 쌓인 먼지를 털어 올리면, 그게 훨씬 크게 터집니다. 사람은 대개 거기서 죽어요.'),
        ],
        gives: ['dust'],
      },
      {
        id: 'why',
        label: '이 일을 하는 이유',
        lines: [
          say('eunho', '…불난 자리에 남은 사람들은 꼭 묻거든요. 왜 났느냐고. 그걸 대답해 주는 사람이 있어야 해서요.'),
          n('그는 더 말하지 않고 다시 바닥으로 눈을 돌렸다.'),
        ],
      },
    ],
    present: {
      checklist: {
        lines: [
          n('윤은호는 내 휴대폰 속 점검표 사진을 한 번 더 들여다보고 돌려줬다.'),
          say('eunho', '전화로 말씀드린 대로입니다. 제가 잘못 봤을 겁니다.'),
          say('eunho', '그보다 메모 쪽을 보세요. 이 창고가 불이 나도 모른다는 걸, 누군가는 2월부터 알고 있었습니다.'),
        ],
      },
      autopsy: {
        lines: [
          say('eunho', '기도에 그을음이 없으면 불 속에서 숨을 안 쉰 겁니다. 그럼 그 불은 사람을 죽이려고 낸 게 아니에요. 이미 죽은 사람을 태우려고 낸 겁니다.'),
          say('eunho', '저라면 시신 둘레의 바닥부터 다시 보겠습니다. 불이 어디를 가장 공들여 태웠는지요.'),
        ],
      },
      stove: {
        lines: [
          say('eunho', '20리터면 방 하나를 적시고도 남습니다. 현장에서 나온 기름과 같은 것인지는 통에 남은 걸로 비교해 보죠.'),
        ],
      },
      collector: {
        lines: [
          say('eunho', '집진기요? 거긴 들어가지 마세요. 먼지를 모아 두는 밀폐된 상자입니다. 밀가루 먼지가 터지는 데 필요한 게 거의 다 들어 있는 곳이에요.'),
        ],
      },
    },
    fallback: [say('eunho', '그건 제가 볼 수 있는 범위 밖이네요. 죄송합니다.')],
  },
  {
    id: 'oh',
    name: '오대성',
    age: 63,
    role: '사장',
    tag: '용의자',
    loc: 'admin',
    desc: '대성제분의 창업주. 40년을 밀가루와 살았다. 화재 당일 밤에는 부산의 장례식장에 있었다고 했다. 창고에는 12억짜리 화재보험이 걸려 있다.',
    topics: [
      {
        id: 'alibi',
        label: '화재 당일 밤의 행적',
        lines: [
          say('oh', '부산에 있었소. 거래처 회장 모친상이라. 저녁 8시 10분에 고속도로에 올랐고 새벽 다섯 시에 돌아왔소. 통행 기록이 다 있을 거요.'),
          n('그가 내민 휴대폰에 통행 내역이 떠 있다. 20시 10분 서해 요금소로 고속도로에 올랐고, 05시 10분 같은 요금소로 내려왔다. 부산의 장례식장에도 확인을 넣었다. 방명록과 CCTV가 일치한다.'),
        ],
        gives: ['ohAlibi'],
      },
      {
        id: 'han',
        label: '한기섭은 어떤 사람이었나',
        lines: [
          say('oh', '스물일곱에 들어와서 20년이오. 돈 문제는 전부 그 사람한테 맡겼지. 나는 밀가루밖에 모르니까.'),
          say('oh', '작년 11월에 장부 숫자가 은행 잔고와 안 맞는다는 얘기가 들려서, 그때 처음 외부 감사 얘기를 꺼냈소. 기섭이 서운해했지. 「사장님, 저를 못 믿으십니까」 하고.'),
        ],
      },
      {
        id: 'alarm',
        label: '창고의 경보',
        when: { clue: 'panelLog' },
        lines: [
          say('oh', '경보요? 꺼져 있었다는 건 나도 오늘 처음 들었소. 설비는 공장장 소관이지.'),
        ],
      },
    ],
    present: {
      checklist: {
        lines: [
          me('점검표입니다. 유지보수 기사가 「복구 요망」이라고 적었고, 관리자가 서명을 거부했다고 돼 있습니다.'),
          say('oh', '…………'),
          say('oh', '새벽마다 헛울려서 동네에서 민원이 들어왔소. 그래서 내가 꺼 두라고 했소. 젊은 기사가 종이를 들이밀면서 서명하라는데, 내가 그런 데 이름 쓸 사람이오? 그냥 가라고 했지.'),
          say('oh', '그 기사가 그럽디다. 「이거 꺼 두시면, 불났을 때 아무도 모릅니다.」 나는 웃었소.'),
          n('오 사장은 더 말하지 않았다.'),
        ],
      },
      fireInsurance: {
        lines: [
          say('oh', '재고를 부풀린 건 맞소. 은행 대출 한도 때문이오. 불을 내려고 한 게 아니라.'),
          say('oh', '40년 지은 공장에 내 손으로 불을 지르겠소?'),
        ],
      },
      audit: {
        lines: [
          me('한 이사 책상에서 나온 감사 통보서입니다. 경리실 금고를 봐야겠습니다.'),
          say('oh', '…금고 번호는 기섭이하고 나만 아오. 나는 6년 동안 열어 본 적이 없지만. 내려갑시다.'),
        ],
        flag: 'safeOpen',
      },
      valveForm: {
        lines: [
          say('oh', '누수 공사? 그런 보고는 받은 적이 없는데. 5천만 원 밑으로는 재무이사 전결이라 내 책상까지 안 올라오오.'),
        ],
      },
      ledger: {
        lines: [
          say('oh', '23억… 내가 사람을 잘못 봤소. 20년을.'),
        ],
      },
    },
    fallback: [say('oh', '그건 내가 모르는 일이오.')],
  },
  {
    id: 'jun',
    name: '오준영',
    age: 34,
    role: '전무 · 사장의 아들',
    tag: '용의자',
    loc: 'admin',
    desc: '사장의 외아들. 2년 전 대기업을 그만두고 내려와 전무가 됐다. 공장을 정리하고 부지를 팔자고 주장해 왔다. 화재 당일 밤에는 집에서 잤다고 했다.',
    topics: [
      {
        id: 'alibi',
        label: '화재 당일 밤의 행적',
        lines: [
          say('jun', '집에서 잤습니다. 새벽에 아버지 전화 받고 알았어요.'),
          me('밤새 집에 계셨다는 거죠.'),
          say('jun', '네.'),
        ],
      },
      {
        id: 'sale',
        label: '공장 매각',
        lines: [
          say('jun', '숨길 것도 없죠. 저는 이 공장 팔자는 쪽입니다. 밀가루 팔아서 남는 돈보다, 이 땅 팔아서 은행에 넣어 둔 이자가 더 커요. 아버지는 그 얘기만 나오면 재떨이를 던지시고요.'),
          say('jun', '그렇다고 제가 불을 질렀겠습니까. 타 버린 창고는 땅값을 올려 주지 않아요.'),
        ],
      },
      {
        id: 'han',
        label: '한기섭은 어떤 사람이었나',
        lines: [
          say('jun', '한 이사님요? 꼼꼼한 분이죠. 저한테는 늘 깍듯했고. …감사 얘기가 나온 뒤로는 저를 피하셨어요.'),
        ],
      },
    ],
    present: {
      gateCctv: {
        lines: [
          me('정문 CCTV입니다. 01시 55분에 전무님 차가 공장 앞에 섭니다. 02시 13분에 떠나고요. 불은 02시 10분에 났습니다.'),
          say('jun', '…………'),
          say('jun', '변호사 불러야 합니까.'),
          n('오준영은 그 일에 대해서는 더 입을 열지 않았다. 불이 난 시각에 정문 앞에 있던 사람이다. 그날 밤 누가 119에 신고했는지부터 서에서 확인해야겠다.'),
        ],
        flag: 'junSilent',
      },
      call119: {
        lines: [
          me('119 상황실 기록입니다. 02시 12분, 대성제분 화재를 처음 신고한 번호가 전무님 휴대폰입니다.'),
          say('jun', '…그것까지 나옵니까.'),
          say('jun', '아니, 보세요. 이걸 보시라고요.'),
          n('오준영이 휴대폰을 내밀었다. 화재 당일 01시 20분에 받은 문자다. 보낸 사람은 한기섭 이사. 화면의 글자를 읽었다.'),
          n('「전무님, 긴히 드릴 말씀이 있습니다. 감사 건입니다. 2시에 공장 정문에서 뵙겠습니다. 혼자 오십시오.」'),
          say('jun', '가서 기다렸는데 안 나와요. 전화도 안 받고. 그러다 창고 지붕에서 연기가 올라오길래 119에 걸었습니다. 이름은 안 댔어요.'),
          say('jun', '불난 공장 앞에, 공장 팔자던 놈이 혼자 서 있었다. 누가 봐도 제가 범인이잖아요. 그래서 도망쳤습니다.'),
        ],
        gives: ['text0120'],
      },
      ledger: {
        lines: [
          say('jun', '23억이요? …아버지가 감사를 부르신 게 맞았네요. 저는 아버지가 괜히 사람을 의심한다고 했는데.'),
        ],
      },
    },
    fallback: [say('jun', '그건 제가 아는 바가 없습니다.')],
  },
  {
    id: 'bae',
    name: '배동수',
    age: 55,
    role: '공장장',
    tag: '용의자',
    loc: 'mill',
    desc: "제분동을 20년 지킨 공장장. 지난달 한기섭의 이름으로 '경영상 해고' 통보를 받았다. 낡은 분전반의 수리를 미룬 책임자로 지목돼 있다.",
    topics: [
      {
        id: 'alibi',
        label: '화재 당일 밤의 행적',
        lines: [
          say('bae', '집에 있었소. 해고당할 놈이 밤에 공장에 뭐 하러 옵니까.'),
        ],
      },
      {
        id: 'repair',
        label: '분전반 수리',
        lines: [
          say('bae', '내가 미뤘다고? 수리 견적을 세 번 올렸소. 세 번 다 한 이사가 반려했지. 「예산 없습니다.」 그 종이 내가 다 갖고 있소.'),
          say('bae', '죽은 사람한테 할 말은 아니지만, 그 양반은 돈 나가는 종이에는 도장을 안 찍는 사람이었소. 스프링클러 잠그는 종이에는 잘만 찍었더만.'),
        ],
      },
      {
        id: 'fired',
        label: '해고',
        lines: [
          say('bae', '20년 일하고 문자 한 통 받았소. 「경영상 사유로…」 한 이사 이름으로. 화가 안 났다면 거짓말이지.'),
          say('bae', '그래도 나는 밀가루 공장에 불 지르는 짓은 안 하오. 이 건물이 어떻게 터지는지 내가 제일 잘 아는데.'),
        ],
      },
    ],
    present: {
      gateCctv: {
        lines: [
          me('23시 5분에 공장장님 화물차가 정문으로 들어옵니다. 집에 있었다고 하셨죠.'),
          say('bae', '…사물함 짐 빼러 왔소. 낮에 오면 사람들 보기 창피해서. 15분 만에 나갔고.'),
          say('bae', '그때 창고 사무실에 불이 켜져 있습디다. 창문으로 보니 한 이사가 누구랑 마주 앉아서 소주를 마시고 있더군. 천 씨였소. 야간 상하차 하는 일용직.'),
          say('bae', '이사가 일용직하고 술을 마신다? 별일이다 싶었지. 한 이사가 자꾸 잔을 채워 주더만. 천 씨는 벌써 고개가 꺾여 있었고.'),
          me('천 씨의 이름은.'),
          say('bae', '천영복. 역 뒤 새벽인력에서 오는 사람이오.'),
        ],
        gives: ['baeSaw'],
      },
      stove: {
        lines: [
          say('bae', '비었다고? 내가 지난 수요일에 가득 채웠소. 꼬리표에 적어 놨잖소.'),
          say('bae', '휴게실 열쇠는 사무동 열쇠함에 거는데, 임원이면 누구나 꺼내 갈 수 있지.'),
        ],
      },
      collector: {
        lines: [
          say('bae', '그 집진기는 1년 전에 세운 기계요. 볼트를 왜 건드려.'),
          say('bae', '…거기 공구함 열쇠를 가진 건 나하고, 설비 예산 본다고 가끔 올라오던 한 이사뿐인데.'),
        ],
      },
    },
    fallback: [say('bae', '그건 나한테 물을 게 아닌 것 같소.')],
  },
  {
    id: 'boss',
    name: '박 반장',
    age: 52,
    role: '강력2팀장',
    tag: '팀',
    loc: 'station',
    desc: "넉 달 전 412호 사건에서 '사고사로 올리라'고 했던 사람. 이번에는 사건을 통째로 넘기고 뒤에서 지켜보고 있다.",
    topics: [
      {
        id: 'lead',
        label: '수사 방향',
        lines: [
          say('boss', '묻지 마라. 이 사건 담당은 너다. 나는 네가 가져오는 증거에 도장만 찍는다.'),
          say('boss', "다만 하나. 불난 사건은 사람들이 다 '누가 질렀나'부터 본다. 나는 30년 해 보니 '죽은 사람이 어떤 사람이었나'가 먼저더라."),
        ],
      },
      {
        id: 'past',
        label: '지난 사건',
        lines: [
          say('boss', '…412호 얘기는 꺼내지 마라.'),
          n('그는 믹스커피를 저었다. 한참 뒤에 혼잣말처럼 덧붙였다.'),
          say('boss', '재수사 서류, 내가 결재했다. 그것만 알아 둬.'),
        ],
      },
    ],
    present: {
      autopsy: {
        lines: [
          say('boss', '불나기 전에 죽었다. 그럼 방화는 덮개고 살인이 본체야. 죽은 사람 주변부터 다시 봐라.'),
        ],
      },
      cremate: {
        lines: [
          say('boss', '유족이 화장하겠다는 걸 막으려면 명분이 있어야 돼. 목요일 아침 전까지, 막을 만한 증거를 내 책상에 올려놔.'),
        ],
      },
      xray: {
        lines: [
          say('boss', '…죽은 사람이 한기섭이 아니다? 서 경위, 그런 말은 증거 두 개 이상으로 해야 한다.'),
        ],
      },
      signature: {
        lines: [
          say('boss', '치과 기록까지 두 달 전에 만들어 놨다. 그럼 이건 홧김에 한 짓이 아니야. 오래 준비한 놈이다.'),
        ],
      },
    },
    fallback: [say('boss', '그건 네가 판단해.')],
  },
  {
    id: 'noh',
    name: '노태준',
    age: 41,
    role: '보험사 조사원',
    tag: '참고인',
    loc: 'station',
    desc: '대성제분의 보험을 맡은 보험사의 조사팀 직원. 불이 난 날 아침부터 경찰서에 와 있다. 사장의 보험 방화를 확신한다.',
    topics: [
      {
        id: 'fire',
        label: '화재보험',
        lines: [
          say('noh', '대성제분 제품 창고, 재고 포함 12억입니다. 문제는 재고예요. 장부상 밀가루가 창고에 다 들어가지도 않는 양입니다. 부풀린 거죠.'),
          say('noh', '경영난, 과다 보험, 한밤중 화재. 형사님, 이건 교과서입니다. 사장이 지른 겁니다.'),
        ],
        gives: ['fireInsurance'],
      },
      {
        id: 'life',
        label: '사망보험',
        lines: [
          say('noh', '아, 그것도 저희 회사 건입니다. 한기섭 씨, 작년 12월에 가입. 사고로 죽으면 10억, 수익자는 부인.'),
          say('noh', '가입 석 달 만의 사망이라 원래는 깐깐하게 봅니다. 가입 2년 안의 자살은 한 푼도 안 나가거든요. 하지만 이건 화재 사고니까 그 조항과는 상관없죠.'),
        ],
        gives: ['lifeInsurance'],
      },
      {
        id: 'survey',
        label: '앞으로의 일정',
        lines: [
          say('noh', '목요일 아침 8시에 저희 조사팀이 공장 전체에 들어갑니다. 창고만이 아니라 제분동, 사일로, 설비 하나하나를 뜯어서 재고와 자산을 셉니다.'),
          say('noh', '그 전에는 경찰 통제선 때문에 아무도 못 들어가고요.'),
        ],
        gives: ['schedule'],
      },
    ],
    present: {
      breaker: {
        lines: [say('noh', '거 보세요. 방화잖습니까. 사장을 잡으세요.')],
      },
      kerosene: {
        lines: [say('noh', '거 보세요. 방화잖습니까. 사장을 잡으세요.')],
      },
      ohAlibi: {
        lines: [say('noh', '부산에 있었다고요? 사람을 시켰겠죠.')],
      },
      autopsy: {
        lines: [say('noh', '…불나기 전에 죽었다고요? 그럼 사망보험은 얘기가 달라지는데.')],
      },
    },
    fallback: [say('noh', '그건 보험 약관과는 상관없는 얘기 같습니다만.')],
  },
  {
    id: 'wife',
    name: '문정아',
    age: 44,
    role: '한기섭의 아내',
    tag: '유족',
    loc: 'home',
    desc: '시계를 보고 남편임을 확인했다. 고등학생 딸이 있다. 장례를 빨리 치르고 싶어 한다.',
    topics: [
      {
        id: 'husband',
        label: '남편에 대해',
        lines: [
          say('wife', '성실한 사람이에요. 20년을 하루도 안 빠지고 출근했어요. 요 몇 달은 잠을 못 잤어요. 새벽에 깨 보면 베란다에 서 있고.'),
          say('wife', '자꾸 미안하다고 했어요. 뭐가 미안하냐고 물으면 그냥, 다 미안하대요.'),
        ],
      },
      {
        id: 'ident',
        label: '신원 확인',
        lines: [
          say('wife', '시계요. 결혼 10주년에 제가 사 준 거예요. 뒷면에 글씨도 새겼고요. 얼굴은… 보지 말라고 하셔서 못 봤어요.'),
        ],
      },
      {
        id: 'dentist',
        label: '치과 기록',
        when: { clue: 'dental' },
        lines: [
          me('남편분이 올 1월에 치과를 세 번 다니셨더군요.'),
          say('wife', '치과요? 그럴 리가요. 그이는 치과를 무서워해서 10년을 안 갔어요. 이가 아프면 진통제를 먹고 버티는 사람이에요.'),
          say('wife', '1월이면… 다녀왔으면 제가 모를 리 없어요. 주사 한 대만 맞아도 며칠을 앓는 사람인데요.'),
        ],
        gives: ['tenYears'],
      },
      {
        id: 'health',
        label: '남편의 건강',
        lines: [
          say('wife', '큰 병은 없었어요. 발목만요. 10년 전에 교통사고로 왼쪽 발목이 부서져서 철심을 박았어요. 비 오는 날이면 쑤신다고 절뚝였죠.'),
          say('wife', '공항 검색대에서 삑 소리가 난다고, 여행 갈 때마다 투덜댔어요.'),
        ],
        gives: ['ankle'],
      },
      {
        id: 'funeral',
        label: '장례',
        lines: [
          say('wife', '목요일 아침에 화장하려고요. 그이가 그랬어요. 「나한테 무슨 일 생기면 질질 끌지 말고 화장부터 해 줘. 납골당도 필요 없어.」'),
          say('wife', '전에는 그런 말을 안 했는데. 이번 겨울 들어서 몇 번이나 그랬어요. 꼭 다짐을 받듯이요.'),
        ],
        gives: ['cremate'],
      },
    ],
    present: {
      lifeInsurance: {
        lines: [
          say('wife', '사망보험이요? 10억…? 저는 몰랐어요. 그이가 그런 걸 들 사람이… 우리 형편에 보험료가 얼마인데요.'),
        ],
      },
      ring: {
        lines: [
          say('wife', '새끼손가락이요? 이상하네요. 그이는 결혼하고 약지에서 반지를 뺀 적이 없어요. 새끼손가락엔 헐렁해서 빠질 텐데.'),
        ],
      },
      ledger: {
        lines: [
          say('wife', '23억이요…? 아니에요, 저희 집 보세요. 20년 된 아파트예요. 그 돈이 다 어디로…'),
        ],
      },
    },
    fallback: [say('wife', '죄송해요. 지금은 잘 모르겠어요.')],
  },
  {
    id: 'go',
    name: '고 소장',
    age: 58,
    role: '새벽인력 소장',
    tag: '참고인',
    loc: 'agency',
    desc: '20년째 이 골목에서 일꾼을 보낸다. 일꾼들의 이름과 사정을 다 기억한다.',
    topics: [
      {
        id: 'chun',
        label: '천영복은 어떤 사람인가',
        lines: [
          say('go', '천 씨? 착한 사람이지. 술을 좀 해서 그렇지. 가족은 없어요. 주소도 요 앞 한솔고시원으로 돼 있고.'),
          say('go', '한 이사님이 꼭 천 씨만 찾았어요. 딴 사람 보내면 돌려보냈다니까. 체격이 자기랑 비슷해서 헌 옷 물려주기 좋다나. 구두도 주고, 점퍼도 주고.'),
          say('go', '천 씨가 그럽디다. 「소장님, 나 이사님 덕에 이도 고쳤어요.」 팔자 폈다고 좋아했지.'),
        ],
        gives: ['sameBuild'],
      },
    ],
    present: {
      baeSaw: {
        lines: [
          say('go', '불난 밤에 공장에 있었다고? 한 이사가 나한테는 그만뒀다고 했는데. 지난 금요일에.'),
          say('go', '…그럼 그 양반이 왜 거짓말을 했을까.'),
        ],
      },
    },
    fallback: [say('go', '그건 내가 알 수가 없지.')],
  },
];

// Notebook entries that only appear once the player has learned of them.
export const EXTRAS = [
  {
    when: { clue: 'chun' },
    name: '천영복',
    age: 49,
    role: '일용직 노동자',
    tag: '행방불명',
    desc: '새벽인력을 통해 석 달간 대성제분 야간 상하차를 했다. 가족이 없고 한솔고시원에 산다. 불이 난 밤 창고 사무실에 있었다.',
  },
];

// Each plays once, on the first move after the player holds that many clues.
export const EVENTS = [
  {
    id: 'autopsy',
    cluesAtLeast: 9,
    lines: [
      n('휴대폰이 울렸다. 국과수 법의관이다.'),
      say('doc', '서지안 형사님이시죠. 대성제분 건 부검 마쳤습니다. 소견서는 경찰서로 보냈습니다. 읽으시고 꼭 저한테 전화 한 번 주십시오. 글로만 보실 내용이 아니라서요.'),
      n('그런 말을 들은 것은 처음이다. 서에 가면 소견서가 와 있을 것이다.'),
    ],
  },
  {
    id: 'cremation',
    cluesAtLeast: 20,
    lines: [
      n('한기섭의 아내 문정아에게서 전화가 왔다. 목소리가 갈라져 있다.'),
      say('wife', '형사님. 시신은 언제 돌려받을 수 있나요. 목요일 아침으로 화장장을 잡았어요. 그이 마지막 부탁이라서요.'),
      n('화장이 끝나면 남는 것은 재뿐이다. 그 재로는 아무것도 물을 수 없다.'),
      n('목요일 아침. 시간이 정해졌다.'),
    ],
  },
];

export const DEDUCTION = {
  // Checked in order; the first missing clue decides which hint the player hears.
  required: [
    { clue: 'breaker', hint: '소방이 발화 지점으로 꼽은 분전반. 아직 직접 보지 않았다.' },
    { clue: 'beads', hint: '합선의 근거라는 녹은 전선. 창고에서 확인해야 한다.' },
    { clue: 'twoOrigins', hint: '불이 어디서 시작됐는지 모른다. 창고 바닥을 봐야 한다.' },
    { clue: 'boxes', hint: '분전반 밑의 잿더미가 마음에 걸린다.' },
    { clue: 'bodySite', hint: '시신이 있던 자리를 아직 보지 않았다. 창고 사무실이다.' },
    { clue: 'kerosene', hint: '사무실 바닥이 유난히 깊게 탔다. 창고 사무실 바닥의 얼룩을 봐야 한다.' },
    { clue: 'gateCctv', hint: '그날 밤 누가 드나들었는지 모른다. 정문 경비실의 CCTV.' },
    { clue: 'panelLog', hint: '창고가 타는 동안 경보는 왜 울리지 않았나. 경비실의 수신기.' },
    { clue: 'checklist', hint: '경비실 수신기 옆에 걸린 종이를 아직 읽지 않았다.' },
    { clue: 'ohAlibi', hint: '사장의 그날 밤 행적을 아직 확인하지 않았다.' },
    { clue: 'call119', hint: '전무의 차가 정문 앞에 서 있었다. 그에게 CCTV를 내밀고, 서에서 그날 밤의 119 신고 기록을 확인해야 한다.' },
    { clue: 'text0120', hint: '02시 12분에 불을 신고한 번호. 전무에게 119 기록을 내밀어야 한다.' },
    { clue: 'baeSaw', hint: '공장장의 차도 그날 밤 들어왔다. CCTV를 내밀어야 한다.' },
    { clue: 'autopsy', hint: '부검 소견이 서에 와 있을 것이다.' },
    { clue: 'dental', hint: '시신이 한기섭이라는 근거가 무엇이었나. 서의 신원 확인 서류.' },
    { clue: 'tenYears', hint: '치과 기록에 대해 부인에게 물어봐야 한다.' },
    { clue: 'ankle', hint: '한기섭의 몸에 대해 부인만큼 아는 사람은 없다.' },
    { clue: 'xray', hint: '발목의 철심. 부검 때 찍은 X선으로 확인할 수 있다. 서에서 법의관에게 부탁하면 된다.' },
    { clue: 'signature', hint: '1월에 그 치과에 간 사람이 누구였는지. 서에서 치과에 전화를 걸어 봐야 한다.' },
    { clue: 'chun', hint: '공장장이 본 일용직 천 씨. 역 뒤 인력사무소로 가야 한다.' },
    { clue: 'sameBuild', hint: '인력사무소 소장에게 천영복에 대해 물어봐야 한다.' },
    { clue: 'alive', hint: '천영복이 살던 고시원. 총무에게 물어봐야 한다.' },
    { clue: 'room', hint: '천영복의 방을 아직 보지 않았다.' },
    { clue: 'lifeInsurance', hint: '한기섭 앞으로 든 보험이 있는지 조사원에게 물어봐야 한다.' },
    { clue: 'audit', hint: '한기섭의 책상을 아직 보지 않았다. 사무동 경리실이다.' },
    { clue: 'ledger', hint: '감사 통보서. 사장에게 내밀면 금고를 열 수 있을 것이다.' },
    { clue: 'schedule', hint: '보험사가 언제 공장에 들어오는지 조사원에게 물어봐야 한다.' },
    { clue: 'collector', hint: '제분동 2층에 멈춰 있는 큰 기계. 아직 살펴보지 않았다.' },
    { clue: 'dust', hint: '제분동은 위험한 곳이라고 했다. 감식 나온 연구관에게 들어 둬야 한다.' },
  ],
  opening: [
    n('수요일 밤 9시. 강력2팀 사무실에는 박 반장과 나뿐이다.'),
    n('그는 믹스커피를 내려놓고 의자를 돌려 나를 봤다. 넉 달 전에는 등을 돌리고 듣던 사람이다.'),
    say('boss', '시작하기 전에 하나만. 412호, 이수아 건. 네가 다시 연 거… 잘했다.'),
    n('뭐라고 답해야 할지 몰라 고개만 숙였다. 반장은 기다려 주지 않았다.'),
    say('boss', '해 봐라. 처음부터.'),
    me('대성제분 화재는 사고가 아닙니다.'),
  ],
  wrong: [
    n('…아니다. 이것으로는 반장을 설득할 수 없다.'),
    say('boss', '다시. 증거로 해라.'),
  ],
  steps: [
    {
      type: 'evidence',
      label: '전기 화재가 아니라는 증거를 제시하라',
      prompt: [say('boss', '소방은 합선이라는데. 전선에 녹은 자국도 있고.')],
      accept: ['breaker'],
      near: {
        beads: [n("녹은 구리는 '원인인지 결과인지 알 수 없다'는 데까지만 말해 준다. 전기가 아예 없었다는 증거가 따로 있다.")],
        twoOrigins: [n('그것은 누가 불을 붙였다는 증거다. 곧 쓴다. 지금 반장이 묻는 것은 합선이다. 그 선에 전기가 흐르지 않았다는 증거가 먼저다.')],
        boxes: [n('그것은 누가 불을 붙였다는 증거다. 곧 쓴다. 지금 반장이 묻는 것은 합선이다. 그 선에 전기가 흐르지 않았다는 증거가 먼저다.')],
        kerosene: [n('그것은 누가 불을 붙였다는 증거다. 곧 쓴다. 지금 반장이 묻는 것은 합선이다. 그 선에 전기가 흐르지 않았다는 증거가 먼저다.')],
      },
      success: [
        me("분전반의 주 차단기가 '꺼짐'에 내려가 있었습니다. 합선으로 떨어졌다면 가운데에 서는 기종입니다. 불이 나기 전에 누가 손으로 내린 겁니다."),
        me('전기가 끊긴 선에서는 합선이 날 수 없습니다. 전선의 구리는 불에 녹은 겁니다.'),
        me('창고의 CCTV와 전등을 끄려고 내렸을 겁니다. 그게 합선 이야기를 스스로 지운다는 것은 몰랐던 거죠.'),
      ],
    },
    {
      type: 'evidence',
      label: '누군가 일부러 붙인 불이라는 증거를 제시하라',
      prompt: [say('boss', '전기가 아니면 뭐야. 담뱃불일 수도 있잖아.')],
      accept: ['twoOrigins', 'boxes', 'kerosene'],
      near: {
        panelLog: [n("경보와 물이 꺼져 있었다는 것은 불이 커진 이유다. 불이 '붙여졌다'는 증거가 먼저다.")],
        valveForm: [n("미리 준비했다는 흔적이다. 하지만 반장은 '담뱃불'을 말하고 있다. 불이 붙은 자리가 직접 말해 주는 증거가 먼저다.")],
        stove: [n('기름이 어디서 왔는지를 말해 주는 통이다. 꼬리표에는 공장장의 글씨가 있고, 휴게실 열쇠는 사무동 열쇠함에 걸려 있다. 누가 썼는지를 말하기에는 아직 이르다. 불이 붙은 자리가 직접 말해 주는 증거가 먼저다.')],
      },
      success: [
        me('발화 지점이 두 곳입니다. 분전반 앞과 사무실 안. 그 사이 바닥은 덜 탔습니다. 따로 붙인 불입니다.'),
        me('분전반 밑에는 상자를 쌓고 시간차 장치를 뒀고, 사무실에는 등유를 뿌렸습니다. 경보는 3주째, 스프링클러는 2주째 꺼져 있었고요.'),
      ],
    },
    {
      type: 'evidence',
      label: '시신이 불나기 전에 죽었다는 증거를 제시하라',
      prompt: [say('boss', '방화라 치자. 그럼 보험사 말대로 사장이 지른 거고, 한기섭은 재수 없게 안에 있었던 거 아니냐.')],
      accept: ['autopsy'],
      near: {
        bodySite: [n('도망친 흔적이 없다는 것은 정황이다. 몸이 말해 주는 증거가 있다.')],
        ohAlibi: [n('사장이 그 시각 부산에 있었다는 것은, 사장이 제 손으로 붙이지 않았다는 데까지만 말해 준다. 사람을 시켰을 수도 있다. 죽은 사람이 재수 없게 거기 있었던 게 아니라는 증거가 필요하다.')],
        gateCctv: [n('그 시각 정문 앞에는 전무의 차가 서 있었다. 하지만 반장이 지금 묻는 것은 누가 왔다 갔느냐가 아니다. 안에 있던 사람이 언제 죽었느냐다.')],
      },
      success: [
        me('부검 소견입니다. 기도에 그을음이 없고, 혈중 일산화탄소는 정상 범위이고, 목 앞쪽의 작은 뼈가 부러져 있습니다. 목이 졸려 죽은 뒤에 불에 탔습니다.'),
        me('사장은 그 시각 부산에 있었습니다. 그리고 재수 없게 안에 있던 것이 아닙니다. 이 불은 그 시신을 태우려고 낸 불입니다.'),
      ],
    },
    {
      type: 'evidence',
      label: '불이 무엇을 지우려 했는지 보여 주는 것을 제시하라',
      prompt: [say('boss', '죽이고 나서 왜 태워. 시신을 숨기려면 딴 방법도 많은데.')],
      accept: ['kerosene'],
      near: {
        dvr: [n('녹화기도 지워야 했다. 맞다. 하지만 불이 가장 공들여 태운 것은 따로 있다.')],
      },
      success: [
        me('등유가 가장 짙게 나온 곳은 머리와 양손이 있던 자리입니다. 방이 아니라 얼굴과 지문을 태운 겁니다.'),
        me('범인은 이 시신이 누구인지를 지우고 싶었습니다. 그런데 이상하죠. 시계와 사원증은 그대로 몸에 남겨 뒀습니다.'),
        say('boss', '…누구인지 지우고, 누구인지 알려 준다.'),
        me('다른 사람으로 알려 주고 싶었던 겁니다.'),
      ],
    },
    {
      type: 'evidence',
      label: '시신이 한기섭이 아니라는 증거를 제시하라',
      prompt: [say('boss', '잠깐. 치과 기록이 일치했다. 그건 어떻게 넘어갈 거냐.')],
      accept: ['xray', 'signature'],
      near: {
        tenYears: [n('그것은 의심의 시작이다. 반장은 물증을 원한다. 몸에 남은 것, 종이에 남은 것.')],
        ankle: [n('그것은 의심의 시작이다. 반장은 물증을 원한다. 몸에 남은 것, 종이에 남은 것.')],
        ring: [n('그것은 의심의 시작이다. 반장은 물증을 원한다. 몸에 남은 것, 종이에 남은 것.')],
        dental: [n('치과 기록은 반장이 든 반론이다. 그 기록이 왜 믿을 수 없는지를 보여 줘야 한다.')],
        cremate: [n('그것은 의심의 시작이다. 반장은 물증을 원한다. 몸에 남은 것, 종이에 남은 것.')],
        sameBuild: [n('누가 그 치과에 갔는지 짐작하게 하는 말이다. 하지만 전해 들은 말이다. 반장은 물증을 원한다. 몸에 남은 것, 종이에 남은 것.')],
      },
      success: [
        me('한기섭은 10년 전 사고로 왼쪽 발목에 철심을 박았습니다. 시신의 발목에는 금속도, 부러졌던 자국도 없습니다.'),
        me('치과 기록은 가짜가 아닙니다. 다만 1월에 그 치과에 간 사람이 한기섭이 아니었습니다. 접수증의 서명이 다르고, 직원은 작업복 입은 남자를 기억합니다.'),
        me('한기섭은 자기 건강보험증을 남에게 빌려줘서, 그 사람의 치아를 자기 이름으로 기록해 뒀습니다. 두 달 전에요.'),
      ],
    },
    {
      type: 'evidence',
      label: '그 방에서 죽은 사람이 누구인지 보여 주는 것을 제시하라',
      prompt: [say('boss', '한기섭이 아니라 치자. 그럼 그 방에서 탄 건 누구야.')],
      accept: ['baeSaw', 'chun', 'sameBuild'],
      near: {
        alive: [n('그것은 그 사람이 살아 있는 것처럼 보이게 한 흔적이다. 불이 난 밤 그가 어디에 있었는지가 먼저다.')],
        room: [n('그 방에 살던 사람의 이름부터 대야 한다. 그리고 불이 난 밤 그가 어디에 있었는지.')],
        ring: [n('반지가 약지가 아니라 새끼손가락에 끼워져 있었다는 것까지만 말해 준다. 그 손의 주인이 누구인지를 대야 한다.')],
        signature: [n('치과에 간 사람이 한기섭이 아니라는 것은 방금 말했다. 그 사람이 누구인지를 대야 한다.')],
      },
      success: [
        me('불이 난 밤 23시, 그 사무실에서 한기섭과 마주 앉아 술을 마시던 사람이 있습니다. 공장장이 봤습니다. 야간 일용직 천영복 씨, 마흔아홉입니다.'),
        me('한기섭은 석 달 동안 인력사무소에 그 사람만 보내 달라고 했습니다. 체격이 자기와 비슷하다는 이유였습니다. 천영복 씨는 「이사님 덕에 이도 고쳤다」고 했고요.'),
        me('가족이 없습니다. 불이 나기 나흘 전에는 한기섭이 인력사무소에 「그만뒀다」고 전화까지 해 뒀습니다. 사라져도 찾을 사람이 없습니다.'),
      ],
    },
    {
      type: 'culprit',
      label: '범인을 지목하라',
      candidates: ['oh', 'jun', 'bae', 'wife', 'noh', 'victim'],
      prompt: [
        say('boss', '…그럼 천영복을 죽이고, 그 손목에 한기섭의 시계를 채운 건 누구야.'),
        me('죽인 사람은—'),
      ],
      accept: ['victim'],
      rebuttal: {
        oh: [say('boss', '사장은 부산에 있었다며. 통행 기록은 네가 가져온 거다.')],
        jun: [say('boss', '전무는 문자를 받고 불려 나온 쪽이야. 119에 신고한 것도 그 친구고. 불 지른 놈이 신고를 하나.')],
        bae: [say('boss', '공장장은 23시 20분에 나갔다. 그리고 천 씨를 봤다고 제 입으로 말한 사람이야. 범인이 그런 말을 하겠냐.')],
        wife: [say('boss', '부인은 발목 철심 얘기를 너한테 해 준 사람이다. 공범이면 그 얘기를 왜 해.')],
        noh: [say('boss', '보험사 직원? 근거가 뭐냐. …없지.')],
      },
      success: [
        me('한기섭입니다. 그는 죽지 않았습니다.'),
        n('박 반장은 한동안 말이 없었다. 식은 커피를 한 모금 마시고, 잔을 내려놓았다.'),
        say('boss', '…죽은 사람이 범인이다. 그런 건 근거가 두 배로 필요해. 왜 그랬는지부터 대 봐라.'),
      ],
    },
    {
      type: 'evidence',
      label: '동기를 증명하는 것을 제시하라',
      prompt: [],
      accept: ['ledger', 'lifeInsurance', 'audit'],
      near: {
        desk: [n('떠날 준비를 했다는 흔적이다. 왜 떠나야 했는지를 보여 주는 것이 필요하다.')],
        fireInsurance: [n('그것은 사장의 돈이다. 한기섭의 돈 얘기를 해야 한다.')],
        valveForm: [n('불을 준비했다는 흔적이다. 반장이 묻는 것은 그 앞이다. 20년을 다닌 회사에 왜 불을 내야 했는지.')],
      },
      success: [
        me('금고에서 이중장부가 나왔습니다. 6년간 23억. 다음 주 월요일에 외부 감사가 시작되면 끝입니다.'),
        me("한기섭은 석 달 전에 사망보험을 들었습니다. 사고사면 10억. 자살로는 안 나옵니다. 그래서 '사고로 죽은 사람'이 필요했습니다."),
        me('천영복 씨를 석 달 동안 곁에 둔 이유입니다. 그리고 불이 난 새벽에는 그 사람의 이름으로 고시원에 들어갔습니다.'),
        say('boss', '…1시 20분 문자는.'),
        me('대비책입니다. 사고로 넘어가면 그만이고, 방화로 드러나면 범인이 따로 있어야 했습니다. 불난 공장 앞에 혼자 서 있던, 공장 팔자던 전무. 어느 쪽이든 한기섭은 피해자로 남습니다.'),
      ],
    },
    {
      type: 'choice',
      label: '한기섭은 오늘 밤 어디에 나타나는가',
      prompt: [
        say('boss', '좋다. 그럼 지금 어디 있냐. 고시원 방은 비었다며.'),
        n('어디 숨어 있는지는 모른다. 하지만 떠나기 전에 반드시 들를 곳이 한 군데 있다.'),
      ],
      options: [
        { id: 'home', label: '한기섭의 집' },
        { id: 'port', label: '삼덕항' },
        { id: 'mill', label: '대성제분 제분동' },
        { id: 'gosiwon', label: '한솔고시원' },
      ],
      accept: ['mill'],
      rebuttal: {
        home: [say('boss', '가족한테 그놈은 죽은 사람이어야 해. 집에는 안 간다.')],
        port: [say('boss', '배는 새벽 5시다. 그 전에 빈손으로 항구에 서 있겠냐. 가져갈 게 있을 거 아니야.')],
        gosiwon: [say('boss', '비운 방에 다시 올 이유가 없지.')],
      },
      success: [
        me('제분동 2층, 멈춘 집진기입니다. 점검구 볼트에만 새 공구 자국이 나 있었습니다. 고시원 방의 영수증에는 몽키스패너와 큰 여행 가방이 찍혀 있었고요. 빼돌린 돈을 거기 숨겨 둔 겁니다.'),
        me('현금 뭉치는 들고 숨어 다닐 수 있는 부피가 아닙니다. 1년째 아무도 안 여는 기계에 넣어 두고, 배가 뜨기 전날 밤에 찾아갈 계산이었을 겁니다. 배는 목요일뿐이니까요.'),
        me('내일 아침 8시면 보험사가 설비를 다 뜯습니다. 배는 새벽 5시고요. 꺼낼 수 있는 것은 오늘 밤뿐입니다.'),
        say('boss', '…차 키 가져와라. 지원은 내가 부른다.'),
      ],
    },
    // The confrontation: one wrong move here ends it.
    {
      type: 'choice',
      stage: '대치',
      sudden: 'burned',
      label: '어떻게 할 것인가',
      brief: '한기섭이 먼지 속에서 라이터를 들고 있다. 박 반장의 손이 권총으로 내려간다.',
      prompt: [
        n('밤 11시 40분. 제분동 맞은편, 시동을 끈 차 안. 지원 인력은 20분 뒤에 온다고 했다.'),
        say('boss', '서 경위.'),
        n('박 반장은 앞 유리만 보고 말했다.'),
        say('boss', '아까 한 말 있잖냐. 나는 그때 무서웠던 거야. 이수아 건을 다시 열면 내가 틀렸다는 게 되니까.'),
        n('나는 대답할 말을 찾지 못했다. 그때, 제분동 2층 창에 손전등 불빛이 스쳤다.'),
        n('우리는 차에서 내렸다. 뒤편 쪽문은 열려 있었다. 2층 계단을 오르자 밀가루 먼지 냄새가 목을 긁었다. 계단참의 비상등 하나가 2층을 희미하게 비추고 있었다.'),
        n('집진기 점검구가 열려 있고, 그 앞에서 한 남자가 검은 가방을 끌어내고 있었다. 모자와 마스크. 왼발에는 체중을 싣지 않고 서 있었다.'),
        me('한기섭 씨. 경찰입니다.'),
        n('한기섭이 돌아봤다. 그리고 손에 든 몽키스패너로 머리 위 대들보를 후려쳤다. 흰 먼지가 눈사태처럼 쏟아져 내려 공기를 채웠다.'),
        say('han', '오지 마. 한 발만 더 오면 다 같이 죽는 거야.'),
        n('한기섭의 다른 손에 뚜껑 달린 기름 라이터가 들려 있다. 내 옆에서 박 반장의 손이 허리의 권총으로 내려갔다.'),
      ],
      options: [
        { id: 'shoot', label: '권총을 뽑아 겨눈다' },
        { id: 'rush', label: '달려들어 라이터를 빼앗는다' },
        { id: 'talk', label: '반장의 손을 막고, 말을 건다' },
      ],
      accept: ['talk'],
      rebuttal: {
        shoot: [
          n('나는 권총을 뽑았다. 겨누는 순간 한기섭의 엄지가 움직였고, 나는 반사적으로 방아쇠를 당겼다.'),
          n('윤은호의 목소리가 뒤늦게 떠올랐다. 총구의 화염도 불씨입니다.'),
        ],
        rush: [
          n('나는 뛰었다. 세 걸음이 모자랐다. 한기섭은 뒷걸음질 치며 라이터를 켰다.'),
          n('떠 있는 가루, 갇힌 공기, 그리고 불씨. 다섯 가지가 다 모였다.'),
        ],
      },
      success: [
        n('나는 박 반장의 손목을 잡았다.'),
        me('반장님, 총은 안 됩니다. 총구에서 나오는 불도 불씨예요. 여기서는 쏘는 순간 터집니다.'),
        n('반장의 손이 권총에서 떨어졌다. 나는 빈손을 들어 보이며 한기섭을 봤다.'),
        me('한기섭 씨. 아무도 안 움직입니다. 라이터 내려놓고 얘기합시다.'),
      ],
    },
    {
      type: 'choice',
      stage: '대치',
      sudden: 'burned',
      label: '다섯 조건 중 지금 없앨 수 있는 것은',
      brief: '가루, 산소, 떠 있는 먼지, 갇힌 공기는 이미 모였다. 불씨 하나만 더해지면 터진다.',
      prompt: [
        n('먼지는 가라앉지 않는다. 이 방은 사방이 막혀 있다. 가루, 산소, 떠 있는 먼지, 갇힌 공기. 불씨 하나만 남았다.'),
        n('하나만 없애면 터지지 않는다. 벽을 훑었다. 전등 스위치, 환풍기 스위치, 소화전, 그리고 바깥 화물 경사로로 통하는 대형 셔터의 수동 체인.'),
      ],
      options: [
        { id: 'light', label: '전등 스위치를 올려 밝게 한다' },
        { id: 'fan', label: '환풍기를 켜서 먼지를 빼낸다' },
        { id: 'shutter', label: '수동 체인을 당겨 대형 셔터를 올린다' },
        { id: 'water', label: '소화전을 열어 물을 뿌린다' },
      ],
      accept: ['shutter'],
      rebuttal: {
        light: [
          n('스위치를 올렸다. 딸깍. 스위치 안에서 작은 불꽃이 튀었다.'),
          n('전등 스위치의 불꽃도 불씨라고, 윤은호는 분명히 말했었다.'),
        ],
        fan: [
          n('환풍기 스위치를 올렸다. 낡은 모터가 불꽃을 튀기며 돌기 시작했고, 바람이 대들보의 먼지를 마저 털어 올렸다.'),
          n('불씨와 먼지를 내 손으로 더했다.'),
        ],
        water: [
          n('소화전 함을 열고 밸브를 돌렸다. 굵은 물줄기가 대들보를 때렸고, 거기 쌓여 있던 먼지가 한꺼번에 털려 올랐다.'),
          n('가라앉히려던 먼지를 내 손으로 더 띄웠다.'),
          say('han', '뭐 하는 거야!'),
          n('놀란 한기섭의 엄지가 라이터를 긁었다.'),
        ],
      },
      success: [
        n('나는 벽을 따라 천천히 옆걸음질 쳐서 체인을 잡았다. 전기가 필요 없는 수동 체인이다.'),
        n('한 뼘, 또 한 뼘. 셔터가 올라가자 밤바람이 밀려 들어왔다. 떠 있던 먼지가 바깥으로 쓸려 나가기 시작했다.'),
        n('갇힌 공기가 풀렸다. 다섯 개 중 하나가 빠졌다.'),
        say('han', '뭐 하는 거야.'),
        me('숨 좀 쉬자는 겁니다. 당신도, 우리도.'),
      ],
    },
    {
      type: 'choice',
      stage: '대치',
      sudden: 'burned',
      label: '한기섭을 어떻게 움직일 것인가',
      brief: '한기섭의 등 뒤에는 먼지로 가득 찬 집진기가 열려 있다. 한기섭은 가방만 가지고 나가게 해 달라고 한다.',
      prompt: [
        n('문제는 한기섭의 등 뒤다. 열린 집진기 점검구. 그 안은 여전히 먼지로 가득 찬 상자다. 한기섭이 저 앞에 서 있는 한, 불씨 하나면 끝이다.'),
        say('han', '가방만 가지고 나가게 해 줘. 그럼 아무도 안 다쳐.'),
        say('han', '나는 죽은 사람으로 남아야 해. 그래야 정아하고 애한테 10억이 가.'),
      ],
      options: [
        { id: 'down', label: '"가방 내려놓고 엎드려!" 하고 소리친다' },
        { id: 'lie', label: '"그 돈은 이미 압수했다"고 말한다' },
        { id: 'door', label: '"가방 들고 저 셔터로 나가세요" 하고 길을 터 준다' },
      ],
      accept: ['door'],
      rebuttal: {
        down: [
          n('내 고함에 한기섭의 어깨가 움찔했다. 궁지에 몰린 사람의 눈이었다.'),
          say('han', '엎드리라고? 여기서 잡히면 다 끝이야.'),
          n('한기섭은 라이터를 켜서, 등 뒤의 점검구 안으로 던졌다. 우리는 그 상자에서 세 걸음 거리에 있었다.'),
        ],
        lie: [
          say('han', '거짓말.'),
          n('한기섭이 확인하려고 점검구 안으로 상체를 넣었다. 가방이 걸려 먼지가 한 번 더 터져 나왔다. 손에는 아직 라이터가 들려 있었다.'),
          n('불꽃이 튄 것은 그 상자 안이었다.'),
        ],
      },
      success: [
        me('가방 들고 저 셔터로 나가세요. 안 막습니다.'),
        n('박 반장이 나를 봤다. 나는 고개를 아주 조금 끄덕였다. 밖에는 지원이 오고 있다. 지금 중요한 것은 한기섭을 저 상자에서 떼어 놓는 것이다.'),
        n('한기섭이 가방을 끌어안고 한 걸음, 두 걸음 셔터 쪽으로 옮겼다. 우리는 반대편 기둥 쪽으로 물러섰다.'),
      ],
    },
  ],
};

export const ENDINGS = {
  true: {
    title: '발화',
    lines: [
      n('한기섭이 셔터 밑에 다다랐을 때, 공장 마당에 순찰차 불빛이 들어왔다. 지원이다.'),
      n('한기섭이 멈췄다. 불빛과 우리를 번갈아 봤다. 속았다고 생각한 얼굴이었다.'),
      say('han', '…나는 어차피 죽은 사람이야.'),
      n('한기섭은 라이터를 켜서, 열린 점검구를 향해 던졌다.'),
      n('박 반장이 먼저 움직였다. 내 어깨를 잡아 콘크리트 기둥 뒤로 밀어 넣고, 그 앞을 몸으로 막았다.'),
      n('집진기가 터졌다. 소리보다 압력이 먼저 왔다. 뜨거운 바람이 열린 셔터로 빠져나갔다.'),
      n('두 번째 폭발은 오지 않았다. 대들보의 먼지가 떨어져 내리며 불길이 한 번 훑고 지나갔지만, 갇히지 않은 공기는 터지지 않았다.'),
      n('귀가 울렸다. 일어나 보니 박 반장이 기둥 앞에 쓰러져 있었다. 뜯겨 날아온 점검구 철문이 반장의 옆에 박혀 있었다.'),
      me('반장님. 반장님!'),
      say('boss', '…시끄럽다. 귀 안 먹었어.'),
      n('반장은 웃으려고 했다. 내 손을 한 번 쥐었다가, 힘이 풀렸다.'),
      say('boss', '서 경위. …사고로, 닫지 마라.'),
      n('그것이 마지막 말이었다.'),
      n('한기섭은 경사로 아래 마당에서 잡혔다. 등에 화상을 입은 채 가방을 끌어안고 있었다. 가방에는 현금 8억과 천영복의 주민등록증이 들어 있었다.'),
      n('나머지 15억은 남아 있지 않았다. 6년 전 주식으로 잃은 3천만 원을 메우려고 처음 회삿돈에 손을 댔고, 그 뒤로는 메운 구멍이 매번 더 큰 구멍이 됐다.'),
      n('나흘 뒤 DNA 대조 결과가 나왔다. 창고 사무실의 시신은 천영복, 49세. 찾아갈 가족이 없어서 장례는 내가 치렀다. 조문객은 고 소장과 나, 둘이었다.'),
      n('문정아는 접견실에서 남편을 5분 보고 나왔다. 나에게는 한마디만 했다.'),
      say('wife', '화장을 막아 주셔서, 고맙습니다.'),
      n('박 반장의 영결식 날은 맑았다. 식이 끝나고 주차장에 서 있는데 누군가 서류 봉투를 내밀었다.'),
      say('eunho', '최종 조사 보고서입니다. 우편으로 보내도 되는데, 설명드릴 게 있어서 직접 왔습니다.'),
      say('eunho', '셔터를 올린 건 형사님이죠. 그게 아니었으면 두 번째가 터졌을 겁니다. 그 건물에 있던 사람은 아무도 못 나왔을 거예요.'),
      me('…한 사람은 못 나왔어요.'),
      n('윤은호는 섣불리 위로하지 않았다. 한참 같이 서 있다가 말했다.'),
      say('eunho', '다음에 뵐 때는, 아무도 다치지 않은 자리였으면 합니다.'),
      n('나는 대답 대신 보고서를 받았다. 봉투는 생각보다 가벼웠다.'),
      n('강력2팀 창가 자리에는 아직 믹스커피 상자가 놓여 있다. 나는 그것을 치우지 않았다.'),
    ],
    epilogue: '한기섭은 살인, 방화, 횡령, 보험사기로 구속됐다. 박 반장은 순직 처리됐고, 서지안은 반장의 마지막 말을 수첩 첫 장에 옮겨 적었다.',
  },
  bad: {
    title: '화장',
    lines: [
      n('말이 꼬였다. 박 반장은 서류를 덮었다.'),
      say('boss', '여기까지다, 서 경위. 이걸로는 화장을 못 막는다. 규칙은 규칙이야.'),
      n('목요일 아침, 시신은 한기섭의 이름으로 화장됐다. 내 보고서는 결재를 받지 못한 채 서랍으로 들어갔다.'),
      n('석 달 뒤 사망보험금 10억이 지급됐다. 그 무렵 한솔고시원 3층 끝 방에는 새 사람이 들어왔다.'),
    ],
    epilogue: '시신은 남의 이름으로 재가 됐다. 하지만 단서는 아직 당신의 수첩에 남아 있다.',
    retry: 'deduce',
  },
  burned: {
    title: '전소',
    lines: [
      n('흰 섬광. 그다음은 기억나지 않는다.'),
      n('눈을 떴을 때는 병원 천장이었다. 박 반장의 자리는 비어 있다고 했다. 한기섭도 나오지 못했다.'),
    ],
    epilogue: '불씨 하나를 막지 못했다. 대치 장면부터 다시 할 수 있다.',
    retry: 'confront',
  },
};
